import React from 'react'
import LegalPageLayout from './LegalPageLayout'

/*
 * POLITIQUE DE CONFIDENTIALITÉ — Clinique du Grand Paris
 *
 * Document RGPD couvrant :
 *  - Le site cliniquedugrandparis.com
 *  - L'assistant conversationnel Ben (ElevenLabs)
 *  - L'intégration LinkedIn (Ben publie sur le profil de David Benaim via l'API officielle, sous validation)
 *  - Prévu : futur outil d'auto-évaluation capillaire avec analyse de photo (consentement explicite RGPD Art. 9)
 *
 * TO BE CONFIRMED BY DAVID before going live :
 *  - Identité exacte du Délégué à la Protection des Données (DPO) ou personne référente RGPD
 *  - Email DPO (si différent de contact@cliniquedugrandparis.com)
 *  - Durées de conservation exactes pour les données patients post-consultation
 *  - Existence éventuelle d'un CRM patient (qui ? quelles données ? où ?)
 *  - Liste exhaustive des sous-traitants (le présent document liste ceux connus)
 */

const PolitiqueConfidentialitePage = () => {
  return (
    <LegalPageLayout
      title="Politique de Confidentialité"
      subtitle="Comment vos données personnelles sont collectées, utilisées et protégées"
      lastUpdated="14 mai 2026"
    >
      <p>
        La <strong>Clinique du Grand Paris</strong> attache une importance particulière à la protection de
        vos données personnelles. La présente politique décrit, en application du Règlement Général sur
        la Protection des Données (RGPD, Règlement UE 2016/679) et de la Loi Informatique et Libertés
        modifiée, comment vos données sont collectées, utilisées, conservées et protégées lorsque vous
        naviguez sur notre site, dialoguez avec notre assistant Ben, ou interagissez avec nos publications
        sur les réseaux sociaux.
      </p>

      <h2>1. Responsable du traitement</h2>
      <div className="legal-card">
        <p>
          <strong>Clinique du Grand Paris</strong> — SAS au capital de 1&nbsp;200&nbsp;€<br />
          37A Avenue d'Iéna, 75116 Paris, France<br />
          SIRET : 840 762 967 00023 — RCS Paris 840 762 967<br />
          Téléphone : 01 48 66 20 35<br />
          Email : <a href="mailto:contact@cliniquedugrandparis.com">contact@cliniquedugrandparis.com</a>
        </p>
        <p>
          <strong>Directeur de la publication :</strong> Dr Lionel Choukroun, chirurgien esthétique (RPPS&nbsp;: 10000580810)
        </p>
      </div>

      <h2>2. Délégué à la Protection des Données (DPO)</h2>
      <p>
        Pour toute demande relative à vos données personnelles, vous pouvez contacter notre référent
        RGPD :
      </p>
      <div className="legal-card">
        <p>
          <strong>Référent RGPD — Clinique du Grand Paris</strong><br />
          Email : <a href="mailto:contact@cliniquedugrandparis.com">contact@cliniquedugrandparis.com</a><br />
          Adresse postale : 37A Avenue d'Iéna, 75116 Paris (mention « RGPD » sur l'enveloppe)
        </p>
      </div>
      <p className="legal-todo">
        À confirmer : David — souhaitez-vous désigner un DPO formel (interne ou externe) ? Pour une
        structure de votre taille, un référent RGPD interne suffit dans la plupart des cas.
      </p>

      <h2>3. Données que nous collectons</h2>

      <h3>3.1 Lorsque vous naviguez sur le site</h3>
      <p>Données collectées automatiquement par les serveurs et outils d'analyse :</p>
      <ul>
        <li>Adresse IP, type de navigateur, système d'exploitation</li>
        <li>Pages visitées, durée de visite, source du trafic (référent)</li>
        <li>Données de connexion (logs serveur)</li>
      </ul>

      <h3>3.2 Lorsque vous nous contactez ou interagissez avec Ben</h3>
      <ul>
        <li>Nom, prénom (si vous les communiquez)</li>
        <li>Adresse email, numéro de téléphone (si vous les communiquez)</li>
        <li>Contenu de votre message ou de votre conversation avec Ben</li>
        <li>Préférences exprimées (type de prestation, contraintes horaires, etc.)</li>
      </ul>

      <h3>3.3 Lorsque vous interagissez avec nos publications sur LinkedIn</h3>
      <p>
        Lorsque vous likez, commentez ou partagez une publication de David Benaim ou de la page CDGP,
        certaines de vos données publiques LinkedIn (nom, photo, titre professionnel, ville) peuvent
        être visibles par nos équipes. <strong>Nous ne collectons ni ne stockons ces données dans nos
        propres bases.</strong> Elles restent gérées par LinkedIn conformément à sa propre politique de
        confidentialité.
      </p>

      <h3>3.4 Cas particulier : outils d'auto-évaluation (à venir)</h3>
      <p>
        La Clinique du Grand Paris pourra à terme proposer un outil d'auto-évaluation capillaire
        permettant de téléverser une photo pour obtenir une analyse indicative. <strong>Si et lorsque cet outil
        sera mis en service, des dispositions spécifiques s'appliqueront :</strong>
      </p>
      <ul>
        <li>
          <strong>Consentement explicite et distinct</strong> au traitement de données biométriques /
          de santé (article 9 du RGPD)
        </li>
        <li>Information préalable sur la finalité, la durée et les destinataires du traitement</li>
        <li>Droit de retrait du consentement à tout moment, avec effacement immédiat des données</li>
        <li>Réalisation préalable d'une Analyse d'Impact relative à la Protection des Données (AIPD)</li>
      </ul>
      <p>
        Cette section sera mise à jour avant l'activation effective de l'outil.
      </p>

      <h2>4. Finalités et base légale des traitements</h2>
      <table>
        <thead>
          <tr>
            <th>Finalité</th>
            <th>Base légale (RGPD)</th>
            <th>Durée de conservation</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Répondre à une demande de contact ou prise de rendez-vous</td>
            <td>Intérêt légitime / exécution de mesures précontractuelles</td>
            <td>3 ans après le dernier contact</td>
          </tr>
          <tr>
            <td>Tenir le dossier médical d'un patient</td>
            <td>Obligation légale (Code de la santé publique)</td>
            <td>20 ans à compter de la dernière consultation</td>
          </tr>
          <tr>
            <td>Statistiques de navigation et amélioration du site</td>
            <td>Intérêt légitime (mesure d'audience, consentement pour les cookies non essentiels)</td>
            <td>25 mois maximum</td>
          </tr>
          <tr>
            <td>Communication marketing (newsletter, retargeting LinkedIn)</td>
            <td>Consentement explicite (Art. 6.1.a RGPD)</td>
            <td>3 ans à compter du dernier contact actif</td>
          </tr>
          <tr>
            <td>Publication de contenus sur les réseaux sociaux par l'assistant Ben</td>
            <td>Exécution d'un mandat explicite du titulaire du compte (David Benaim)</td>
            <td>Durée du mandat ; jetons d'accès supprimés en cas de révocation</td>
          </tr>
        </tbody>
      </table>

      <h2>5. Destinataires et sous-traitants</h2>
      <p>Vos données peuvent être traitées par les prestataires suivants, agissant en qualité de sous-traitants au sens du RGPD :</p>
      <table>
        <thead>
          <tr>
            <th>Sous-traitant</th>
            <th>Finalité</th>
            <th>Localisation</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>OVH SAS</strong></td>
            <td>Hébergement du site web et des données</td>
            <td>France (UE)</td>
          </tr>
          <tr>
            <td><strong>ElevenLabs</strong></td>
            <td>Moteur conversationnel de l'assistant Ben (synthèse vocale et compréhension du langage)</td>
            <td>États-Unis — encadré par des Clauses Contractuelles Types</td>
          </tr>
          <tr>
            <td><strong>Anthropic / OpenAI</strong></td>
            <td>Modèles de langage utilisés pour Ben et pour la rédaction de contenus marketing</td>
            <td>États-Unis — encadré par des Clauses Contractuelles Types</td>
          </tr>
          <tr>
            <td><strong>LinkedIn Corporation</strong></td>
            <td>
              Publication de contenus par Ben sur le profil personnel de David Benaim,
              via l'API officielle LinkedIn, après validation explicite du David Benaim
            </td>
            <td>États-Unis — encadré par des Clauses Contractuelles Types</td>
          </tr>
          <tr>
            <td><strong>Ricopoulos</strong></td>
            <td>Développement, maintenance et exploitation technique du site et de l'assistant Ben</td>
            <td>France / UE</td>
          </tr>
        </tbody>
      </table>
      <p>
        Aucune de vos données n'est cédée ou louée à des tiers à des fins commerciales.
      </p>

      <h2>6. Intégration LinkedIn — précisions</h2>
      <p>
        Dans le cadre de la communication digitale de la Clinique du Grand Paris, l'assistant Ben rédige
        des publications destinées au profil LinkedIn personnel de M.&nbsp;David Benaim, Directeur de la
        clinique, ainsi qu'à la page officielle CDGP. <strong>Aucune publication n'est mise en ligne
        sans validation préalable et explicite de M.&nbsp;David Benaim lui-même.</strong>
      </p>
      <p>Le processus est le suivant :</p>
      <ol>
        <li>Ben prépare un brouillon de publication (texte, image, vidéo éventuelle).</li>
        <li>Le brouillon est présenté à M.&nbsp;David Benaim via une interface privée.</li>
        <li>M.&nbsp;David Benaim valide, modifie ou rejette la publication.</li>
        <li>Une fois validée, la publication est mise en ligne via l'API officielle LinkedIn.</li>
      </ol>
      <p>
        Cette intégration repose exclusivement sur l'<strong>API officielle LinkedIn</strong>
        (« <em>Share on LinkedIn</em> » et « <em>Sign In with LinkedIn using OpenID Connect</em> »),
        avec les autorisations strictement minimales nécessaires à la publication (scope
        <em> w_member_social</em>). M.&nbsp;David Benaim peut à tout moment révoquer ces autorisations
        depuis ses paramètres LinkedIn.
      </p>
      <p>
        <strong>Ce service ne lit ni vos messages privés, ni votre liste de contacts, ni vos données
        personnelles LinkedIn.</strong> Aucune donnée personnelle des utilisateurs ayant interagi avec
        les publications n'est extraite, stockée ou exploitée.
      </p>

      <h2>7. Vos droits</h2>
      <p>Conformément au RGPD, vous disposez des droits suivants sur vos données :</p>
      <ul>
        <li><strong>Droit d'accès</strong> aux données vous concernant (Art. 15)</li>
        <li><strong>Droit de rectification</strong> des données inexactes (Art. 16)</li>
        <li><strong>Droit à l'effacement</strong> (« droit à l'oubli ») dans les conditions prévues (Art. 17)</li>
        <li><strong>Droit à la limitation</strong> du traitement (Art. 18)</li>
        <li><strong>Droit à la portabilité</strong> des données (Art. 20)</li>
        <li><strong>Droit d'opposition</strong> au traitement, notamment à des fins de prospection (Art. 21)</li>
        <li><strong>Droit de retirer votre consentement</strong> à tout moment, lorsque le traitement est fondé sur le consentement</li>
        <li><strong>Droit de définir des directives</strong> relatives au sort de vos données après votre décès</li>
      </ul>
      <p>
        Pour exercer l'un de ces droits, contactez-nous à
        <a href="mailto:contact@cliniquedugrandparis.com"> contact@cliniquedugrandparis.com</a>.
        Nous répondons sous un délai d'un mois maximum. Une pièce d'identité pourra vous être demandée
        en cas de doute raisonnable sur votre identité.
      </p>
      <p>
        Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation
        auprès de la <strong>Commission Nationale de l'Informatique et des Libertés (CNIL)</strong> —
        3 Place de Fontenoy, 75007 Paris,
        <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer"> www.cnil.fr</a>.
      </p>

      <h2>8. Cookies et traceurs</h2>
      <p>
        Le site cliniquedugrandparis.com utilise des cookies techniques nécessaires à son
        fonctionnement (session, sécurité). Des cookies optionnels (mesure d'audience, retargeting
        LinkedIn) peuvent être déposés uniquement après votre consentement explicite, via le bandeau
        de gestion des cookies.
      </p>
      <p>
        Vous pouvez modifier vos préférences ou retirer votre consentement à tout moment en accédant à
        la <a href="/cookies">page de gestion des cookies</a>.
      </p>

      <h2>9. Sécurité des données</h2>
      <p>
        Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger
        vos données contre toute destruction, perte, altération, divulgation non autorisée ou accès
        illicite : chiffrement TLS pour toutes les communications, chiffrement au repos pour les jetons
        d'accès sensibles (LinkedIn notamment), contrôles d'accès stricts, journalisation des accès
        aux données médicales.
      </p>
      <p>
        Les jetons d'authentification LinkedIn permettant à Ben de publier sont stockés chiffrés
        (AES-256-GCM) sur un serveur dédié hébergé en France. Seul le code de notre service y a accès.
      </p>

      <h2>10. Photographies de patients</h2>
      <p>
        Les photographies de patients diffusées sur le site et sur nos publications réseaux sociaux
        le sont avec leur consentement écrit, libre, éclairé et révocable. Toute personne y figurant
        peut à tout moment demander le retrait de son image en contactant la clinique. L'anonymat
        (yeux floutés, bandeau identitaire) est appliqué systématiquement conformément aux règles
        de la publicité médicale.
      </p>

      <h2>11. Modifications de la présente politique</h2>
      <p>
        La présente politique peut être modifiée pour tenir compte d'évolutions légales, techniques ou
        des pratiques de la Clinique du Grand Paris. La date de dernière mise à jour figure en haut de
        ce document. En cas de modification substantielle, nous vous en informerons par un moyen
        approprié (bandeau d'information, email pour les patients déjà identifiés).
      </p>

      <h2>12. Contact</h2>
      <div className="legal-card">
        <p>
          Pour toute question relative à la présente politique :<br />
          <strong>Email :</strong> <a href="mailto:contact@cliniquedugrandparis.com">contact@cliniquedugrandparis.com</a><br />
          <strong>Téléphone :</strong> 01 48 66 20 35<br />
          <strong>Adresse :</strong> Clinique du Grand Paris, 37A Avenue d'Iéna, 75116 Paris
        </p>
      </div>
    </LegalPageLayout>
  )
}

export default PolitiqueConfidentialitePage
